// The content of the pin's popover: where a source is saved. «Preferiti»
// (the pinned sources) is always ticked and cannot be unticked; the user's
// feeds are folders to choose; «Nuovo gruppo fonti» creates one. With the pin
// on, the same popover manages the folders and offers «Rimuovi dai
// preferiti». Presentational: the panel (SourcesPanel) makes the calls.
// Built from Platea's own elements: the rows with a tick are the rows of
// the sources list (src/components/sidebar/SourceList.tsx), the field is
// the one of the feed editor (FeedEditor.tsx), the buttons and the notes
// are the ones of a status row (src/components/PanelStatus.tsx).
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Label, TextField } from "@heroui/react";

import { PanelStatus, STATUS_BUTTON_CLASS } from "../../components/PanelStatus";
import { CheckToggle } from "../../components/sidebar/CheckToggle";
import { NAME_IN_USE, NAME_RESERVED, type Membership, type NameProblem } from "./adapt";

export const FAVOURITES_NOTE = "Il pin mette la fonte tra i preferiti.";
export const PARTLY_NOTE = "Alcune sezioni di questo ente non sono tra i preferiti.";
export const LEAVES_NOTE = "Esce anche da: ";
export const LOADING_FOLDERS = "Caricamento delle cartelle…";
export const FOLDERS_FAILED = "Cartelle non disponibili.";

const NAME_PROBLEM: Record<NameProblem, string> = { "in-use": NAME_IN_USE, reserved: NAME_RESERVED };

/** A folder of the user, with how much of the source it holds: all of it (ticked), some of it («in parte»), none. */
export type FolderRow = { id: string; name: string; held: Membership };

/** A folder the user changed: into it (every section of the source) or out of it. */
export type FolderChoice = { id: string; checked: boolean };

export type FolderChooserError = { message: string; onRetry?: () => void };

type FolderChooserProps = {
  /** "save": the pin is off, «Salva» pins the source; "manage": the pin is on, the folders are managed. */
  mode: "save" | "manage";
  /** Some sections of the source are not favourites yet (manage mode). */
  partly?: boolean;
  /** The user's folders; null while they are not known. */
  folders: FolderRow[] | null;
  foldersStatus: "loading" | "error" | "ready";
  onRetryFolders: () => void;
  /** Why a new folder cannot take a name, before any call. */
  nameProblem: (name: string) => NameProblem | null;
  /** The folders the source leaves with «Rimuovi dai preferiti» (manage mode). */
  leaves?: string[];
  /** A call is on its way: the buttons wait, nothing can be changed. */
  pending: boolean;
  error: FolderChooserError | null;
  /** «Salva»: the folders changed, and the name of a new one, if any. */
  onSave: (choices: FolderChoice[], newFolder: string | null) => void;
  /** «Aggiungi le altre sezioni» (manage mode, partly). */
  onAddMissing?: () => void;
  /** «Rimuovi dai preferiti» (manage mode). */
  onRemove?: () => void;
  onCancel: () => void;
};

export const PARTLY_ROW = "(in parte)";

const ROW_CLASS = "flex items-center gap-3 py-2";
const LABEL_CLASS = "text-sm leading-6 text-accent";
/** The note of a row: the muted text of a status row (src/components/PanelStatus.tsx:27). */
const NOTE_CLASS = "text-sm leading-6 text-muted";

export function FolderChooser({
  mode,
  partly = false,
  folders,
  foldersStatus,
  onRetryFolders,
  nameProblem,
  leaves = [],
  pending,
  error,
  onSave,
  onAddMissing,
  onRemove,
  onCancel,
}: FolderChooserProps) {
  // what the folders hold as they came: a row is ticked when the folder holds the whole source
  const initial = useMemo(() => new Map((folders ?? []).map((f) => [f.id, f.held])), [folders]);
  // the rows the user clicked, as differences from `initial`: the folders can arrive or change while
  // the popover is open without the clicks, the name or the focus being lost
  const [touched, setTouched] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [name, setName] = useState("");
  const root = useRef<HTMLDivElement>(null);

  const isTicked = (id: string) => touched.get(id) ?? initial.get(id) === "all";
  // a click on a row held in part is always a change: the whole source in, or out, of the folder
  const choices: FolderChoice[] = [...touched]
    .filter(([id, checked]) => initial.has(id) && (checked ? "all" : "none") !== initial.get(id))
    .map(([id, checked]) => ({ id, checked }));
  const trimmed = name.trim();
  const problem = trimmed === "" ? null : nameProblem(trimmed);
  // in save mode «Salva» always has the pin to put; in manage mode it needs a change or a new folder
  const canSave = !pending && problem === null && (mode === "save" || choices.length > 0 || trimmed !== "");

  const toggle = (id: string, checked: boolean) => {
    if (pending) return;
    setTouched((map) => new Map(map).set(id, checked));
  };

  // the first tick that can be changed takes the focus when the folders are there (at the opening, or
  // when they arrive later), unless the user has already moved it (typing a name while they loaded)
  const ready = folders !== null;
  useEffect(() => {
    if (!ready || !root.current) return;
    const active = document.activeElement;
    if (active instanceof Node && root.current.contains(active)) return;
    root.current.querySelector<HTMLElement>("[role=checkbox]:not(:disabled)")?.focus();
  }, [ready]);

  let rows: React.ReactNode;
  if (foldersStatus === "loading") rows = <PanelStatus indent={false}>{LOADING_FOLDERS}</PanelStatus>;
  else if (foldersStatus === "error" || !folders)
    rows = (
      <PanelStatus role="note" indent={false} onRetry={onRetryFolders}>
        {FOLDERS_FAILED}
      </PanelStatus>
    );
  else
    rows = folders.map((folder) => (
      <div key={folder.id} className={ROW_CLASS}>
        <CheckToggle
          checked={isTicked(folder.id)}
          label={`Seleziona ${folder.name}`}
          onChange={(checked) => toggle(folder.id, checked)}
        />
        <span className={LABEL_CLASS}>{folder.name}</span>
        {/* the folder holds some sections of the source only, and the user has not chosen yet */}
        {folder.held === "some" && !touched.has(folder.id) && <span className={NOTE_CLASS}>{PARTLY_ROW}</span>}
      </div>
    ));

  return (
    <div ref={root} className="flex flex-col gap-2.5">
      <span className="text-sm leading-6 font-medium text-accent">Salva in</span>

      <div className="flex flex-col">
        <div className={ROW_CLASS}>
          <CheckToggle checked readOnly label="Seleziona Preferiti" />
          <span className={LABEL_CLASS}>Preferiti</span>
        </div>
        {mode === "save" && (
          <PanelStatus role="note" indent={false}>
            {FAVOURITES_NOTE}
          </PanelStatus>
        )}
        {rows}
      </div>

      <TextField value={name} onChange={setName} isDisabled={pending} className="flex w-full flex-col gap-1">
        <Label className="text-sm leading-6 font-medium text-accent">Nuovo gruppo fonti</Label>
        <Input
          placeholder="Nome del gruppo"
          className="h-9 w-full rounded-xl border-0 bg-field-background px-3 text-sm leading-5 shadow-field"
        />
      </TextField>
      {problem && (
        <PanelStatus role="note" indent={false}>
          {NAME_PROBLEM[problem]}
        </PanelStatus>
      )}

      {mode === "manage" && partly && (
        <PanelStatus role="note" indent={false}>
          {PARTLY_NOTE}
        </PanelStatus>
      )}
      {mode === "manage" && (
        <div className="flex flex-wrap items-center gap-2.5">
          {partly && onAddMissing && (
            <Button size="sm" isDisabled={pending} onPress={onAddMissing} className={STATUS_BUTTON_CLASS}>
              Aggiungi le altre sezioni
            </Button>
          )}
          {onRemove && (
            <Button size="sm" isDisabled={pending} onPress={onRemove} className={STATUS_BUTTON_CLASS}>
              Rimuovi dai preferiti
            </Button>
          )}
        </div>
      )}
      {mode === "manage" && leaves.length > 0 && (
        <PanelStatus role="note" indent={false}>
          {LEAVES_NOTE}
          {leaves.join(", ")}.
        </PanelStatus>
      )}

      {error && (
        <PanelStatus role="alert" indent={false} onRetry={error.onRetry}>
          {error.message}
        </PanelStatus>
      )}

      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          size="sm"
          isDisabled={!canSave}
          onPress={() => onSave(choices, trimmed === "" ? null : trimmed)}
          className={STATUS_BUTTON_CLASS}
        >
          Salva
        </Button>
        <Button size="sm" isDisabled={pending} onPress={onCancel} className={STATUS_BUTTON_CLASS}>
          Annulla
        </Button>
      </div>
    </div>
  );
}
